import React from 'react';
import DeleteBucketIcon from '@svg/delete_bucket_icon';

import { Modal, Button, LoadingButton } from '@common/index';
import { Alert, Stack } from '@mui/material';
import { useTheme } from '../../styles/theme';

import { useDispatch, useSelector } from 'react-redux';
import { flowActions } from '@store/slices';
import { AppDispatch, State } from '@store/index';
import { useSnackbar } from 'notistack';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Clear } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { RequestError } from '@requests/common';
import {
  deleteAllObjectsFromBucket,
  deleteBucket,
  getBucket,
  getBucketFiles,
  GetBucketFilesParams,
  GetBucketParams,
  TelnyxBucket,
  TelnyxBucketFile,
} from '@requests/buckets';

export const DeleteBucket: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const theme = useTheme();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const clearBucketName = useSelector((state: State) => state.flow.clearBucket);
  const deleteBucketName = useSelector((state: State) => state.flow.deleteBucket);
  const bucketToDeleteName = clearBucketName || deleteBucketName;

  const setOpen = (val: string | null) =>
    clearBucketName ? dispatch(flowActions.setClearBucket(val)) : dispatch(flowActions.setDeleteBucket(val));

  const { service_id, bucketName } = useParams();
  const { enqueueSnackbar } = useSnackbar();

  const {
    data: bucket = null,
    error: bucketError,
    isLoading: isBucketLoading,
    refetch: refetchBucket,
    isFetching: isFetchingBuckets,
  } = useQuery<TelnyxBucket, RequestError, TelnyxBucket, [string, string, GetBucketParams]>({
    queryKey: ['globalFiles', 'bucket', { service_id: service_id, bucket_name: bucketToDeleteName }],
    queryFn: getBucket,
    enabled: Boolean(service_id && bucketToDeleteName),
  });

  const {
    data: files = [],
    status: fileStatus,
    error: filesError,
    refetch: refetchFiles,
    isFetching: isFetchingFiles,
  } = useQuery<TelnyxBucketFile[], RequestError, TelnyxBucketFile[], [string, string, GetBucketFilesParams]>({
    queryKey: [
      'globalFiles',
      'files',
      { service_id: service_id as string, bucket_name: bucketToDeleteName as string, region: bucket?.region as string },
    ],
    queryFn: getBucketFiles,
    enabled: Boolean(bucket?.region && bucketToDeleteName),
    refetchInterval: false,
    retry: false,
  });

  const { mutateAsync: clear, status: clearStatus } = useMutation({
    mutationFn: deleteAllObjectsFromBucket,
    onSuccess: (data) => {
      if (!data) return;

      if (data?.error) {
        enqueueSnackbar(data?.error?.error || data?.error, {
          variant: 'error',
          autoHideDuration: 10000,
        });

        enqueueSnackbar('Failed to clear bucket!', {
          variant: 'error',
          autoHideDuration: 10000,
        });

        return;
      }

      if (clearBucketName) setOpen(null);
      queryClient.invalidateQueries({ queryKey: ['globalFiles'] });
      enqueueSnackbar('All files cleared from bucket succesfully!', { variant: 'success', autoHideDuration: 3000 });
    },
  });

  const isClearLoading = clearStatus === 'pending';

  const { mutateAsync: discard, status: discardStatus } = useMutation({
    mutationFn: deleteBucket,
    onSuccess: (data) => {
      if (!data) return;

      queryClient.invalidateQueries({ queryKey: ['globalFiles'] });

      if (data?.error) {
        enqueueSnackbar('Failed to delete bucket!', {
          variant: 'error',
          autoHideDuration: 10000,
        });

        enqueueSnackbar(data?.error?.error || data?.error, {
          variant: 'error',
          autoHideDuration: 10000,
        });

        return;
      }

      setOpen(null);
      if (bucketName === data.bucket_name) navigate(`/${service_id}/buckets`);
      enqueueSnackbar('Deleted bucket successfully!', { variant: 'success', autoHideDuration: 3000 });
    },
  });

  const isDeleteLoading = discardStatus === 'pending';

  const deleteBucketByName = () => {
    if (!bucketToDeleteName) return;

    enqueueSnackbar('Deleting bucket...', {
      variant: 'info',
      autoHideDuration: 3000,
    });

    if (bucket) discard({ bucket_name: bucketToDeleteName, region: bucket.region, service_id: service_id as string });
    else {
      enqueueSnackbar('Could not find bucket, please refresh and try again.', {
        variant: 'error',
        autoHideDuration: 3000,
      });
    }
  };

  const clearBucket = () => {
    if (!bucketToDeleteName) return;

    enqueueSnackbar('Clearing bucket...', {
      variant: 'info',
      autoHideDuration: 3000,
    });

    if (bucket) clear({ bucket_name: bucketToDeleteName, region: bucket.region, service_id: service_id as string });
    else {
      enqueueSnackbar('Could not find bucket, please refresh and try again.', {
        variant: 'error',
        autoHideDuration: 3000,
      });
    }
  };

  const isBucketDeletable =
    ((files?.length === 0 && fileStatus === 'success' && !isClearLoading) || isDeleteLoading) && !clearBucketName;

  if (bucketError || filesError) {
    return (
      <Modal
        header={isBucketDeletable ? 'Delete Bucket' : 'Clear Bucket'}
        identifier={bucketToDeleteName}
        open={Boolean(bucketToDeleteName)}
        setOpen={setOpen as (val: boolean | string | null) => void}
        page={2}
        total_pages={2}
        background={theme.telnyx.red}
        icon={<DeleteBucketIcon height={20} color={theme.telnyx.red} />}
        info={`Unable to retrieve information about the bucket. Please re-try again shortly.`}
        body={<Alert severity='error'>{bucketError?.response?.data?.error || filesError?.response?.data?.error}</Alert>}
        actions={
          <Stack direction='row' spacing={1}>
            <Button small onClick={() => setOpen(null)} disabled={isDeleteLoading}>
              Back
            </Button>
            <LoadingButton
              small
              customColor='red'
              onClick={() => {
                refetchFiles();
                refetchBucket();
              }}
              loading={isDeleteLoading}
              minWidth='70px'
            >
              Retry
            </LoadingButton>
          </Stack>
        }
      />
    );
  }

  if (isBucketDeletable)
    return (
      <Modal
        header='Delete Bucket'
        identifier={bucketToDeleteName}
        open={Boolean(bucketToDeleteName)}
        setOpen={setOpen as (val: boolean | string | null) => void}
        page={2}
        total_pages={2}
        background={theme.telnyx.red}
        icon={<DeleteBucketIcon height={20} color={theme.telnyx.red} />}
        info={`Deleting this bucket will remove it from Telnyx storage. You can always re-create the bucket by creating a new bucket with the same name.`}
        actions={
          <Stack direction='row' spacing={1}>
            <Button small onClick={() => setOpen(null)} disabled={isDeleteLoading}>
              Back
            </Button>
            <LoadingButton
              small
              customColor='red'
              onClick={deleteBucketByName}
              loading={isDeleteLoading || fileStatus === 'pending' || isBucketLoading}
              minWidth='70px'
            >
              Delete
            </LoadingButton>
          </Stack>
        }
      />
    );

  return (
    <Modal
      header='Clear Bucket'
      identifier={bucketToDeleteName}
      open={Boolean(bucketToDeleteName)}
      setOpen={setOpen as (val: boolean | string | null) => void}
      page={clearBucketName ? undefined : 1}
      total_pages={clearBucketName ? undefined : 2}
      background={theme.telnyx.orange}
      icon={<Clear />}
      info={
        clearBucketName
          ? `Clearing this bucket will remove all of the files from the bucket. This action is asynchronous and you will see a loader until it is complete. Please expect longer load times for buckets with a large amount of files.`
          : `This bucket is not empty and therefore cannot be deleted. If you would like to continue with the deletion process, please clear all files from the bucket first.`
      }
      actions={
        <Stack direction='row' spacing={1}>
          <Button small onClick={() => setOpen(null)} disabled={isClearLoading}>
            Back
          </Button>
          <LoadingButton
            loading={
              isClearLoading || fileStatus === 'pending' || isBucketLoading || isFetchingFiles || isFetchingBuckets
            }
            small
            customColor='orange'
            minWidth='115px'
            onClick={clearBucket}
            disabled={!files?.length || files.length === 0}
          >
            {files ? `Clear (${files.length}) Files` : 'Clear Files'}
          </LoadingButton>
        </Stack>
      }
    />
  );
};
